/**
 * Color Resolver Utilities
 *
 * Resolves Figma colors (0-1 float) to RGB (0-255 int) values.
 * Handles variable references by resolving to concrete values.
 */

import type { ColorValue, GradientStop, RGBAColor } from '../types/component-tree.js'

/**
 * Convert Figma color (0-1 float) to RGB (0-255 int)
 */
export function figmaColorToRGB(figmaColor: any): RGBAColor {
  return {
    r: Math.round((figmaColor.r ?? 0) * 255),
    g: Math.round((figmaColor.g ?? 0) * 255),
    b: Math.round((figmaColor.b ?? 0) * 255),
    a: figmaColor.a ?? 1,
  }
}

/**
 * Convert RGB color to hex string
 * @param color - RGB color object
 * @returns Hex color string (e.g., "#DBEAFE" or "#DBEAFE80" with alpha)
 */
export function rgbToHex(color: RGBAColor): string {
  const r = color.r.toString(16).padStart(2, '0')
  const g = color.g.toString(16).padStart(2, '0')
  const b = color.b.toString(16).padStart(2, '0')

  // Include alpha channel if not fully opaque
  if (color.a < 1) {
    const a = Math.round(color.a * 255)
      .toString(16)
      .padStart(2, '0')
    return `#${r}${g}${b}${a}`.toUpperCase()
  }

  return `#${r}${g}${b}`.toUpperCase()
}

/**
 * Resolve a single paint to ColorValue
 * @param paint - Figma paint object
 */
export function resolvePaint(paint: any): ColorValue | null {
  if (!paint || !paint.type) {
    return null
  }

  switch (paint.type) {
    case 'SOLID': {
      const rgbColor = figmaColorToRGB(paint.color)
      return {
        type: 'solid',
        color: rgbToHex(rgbColor),
      }
    }

    case 'GRADIENT_LINEAR':
    case 'GRADIENT_RADIAL':
    case 'GRADIENT_ANGULAR':
    case 'GRADIENT_DIAMOND':
      return resolveGradientPaint(paint)

    // Image fills, video fills not supported yet
    case 'IMAGE':
    case 'VIDEO':
    case 'EMOJI':
      return null

    default:
      return null
  }
}

/**
 * Resolve gradient paint
 * @param paint - Figma gradient paint object
 */
function resolveGradientPaint(paint: any): ColorValue | null {
  if (!paint.gradientStops || paint.gradientStops.length === 0) {
    return null
  }

  const gradientType = mapGradientType(paint.type)
  const stops: GradientStop[] = paint.gradientStops.map((stop: any) => {
    const rgbColor = figmaColorToRGB(stop.color)
    return {
      color: rgbToHex(rgbColor),
      position: stop.position ?? 0,
    }
  })

  return {
    type: 'gradient',
    gradient: {
      type: gradientType,
      stops,
    },
  }
}

/**
 * Map Figma gradient type to web gradient type
 */
function mapGradientType(figmaType: string): 'linear' | 'radial' | 'angular' {
  switch (figmaType) {
    case 'GRADIENT_LINEAR':
      return 'linear'
    case 'GRADIENT_RADIAL':
    case 'GRADIENT_DIAMOND':
      return 'radial'
    case 'GRADIENT_ANGULAR':
      return 'angular'
    default:
      return 'linear'
  }
}

/**
 * Resolve paint array to ColorValue array
 * Filters out null values (unsupported paint types)
 * @param paints - Array of Figma paint objects
 */
export function resolveColors(
  paints: any[] | undefined,
): ColorValue[] | undefined {
  if (!paints || paints.length === 0) {
    return undefined
  }

  const resolved = paints
    .map((paint) => resolvePaint(paint))
    .filter((color): color is ColorValue => color !== null)

  return resolved.length > 0 ? resolved : undefined
}

/**
 * Resolve a single color from paint array (takes first solid color)
 * Useful for text colors which are typically single colors
 * @param paints - Array of Figma paint objects
 */
export function resolveSingleColor(
  paints: any[] | undefined,
): ColorValue | undefined {
  const colors = resolveColors(paints)
  return colors?.[0]
}
